import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService } from '@wsphere/staff/web';
import { firstValueFrom, map, switchMap, tap } from 'rxjs';
import { ProvisioningSessionService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-provision-distributor',
  templateUrl: './provision-distributor.component.html',
  standalone: false,
})
export class ProvisionDistributorComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private securitySvc: SecurityService,
    private sessionSvc: ProvisioningSessionService,
    private toastSvc: ToastService
  ) {}

  @HostBinding('class') _class = 'pb-24 px-6';

  @Output() completed = new EventEmitter();
  @Output() distributor = new EventEmitter<Distributor.Model | null>();

  distributorControl = new FormControl(null as Distributor.Model | null);

  globalDistributors$ = this.securitySvc.globalDistributors$;

  hasGlobalAccess$ = this.globalDistributors$.pipe(
    switchMap(() =>
      Promise.all([
        this.securitySvc.hasAccess('wrt_CreateWarranty', null),
        this.securitySvc.hasAccess('wrt_CreateDraftWarranty', null),
      ])
    ),
    map(([hasAccess, hasDraftAccess]) => hasAccess || hasDraftAccess)
  );

  protected session$ = this.sessionSvc.getCurrentSession().pipe(
    tap(session => {
      // Only pre-fill on the initial load (control is empty). If the user has
      // already selected a distributor, do not overwrite their choice — this
      // prevents a later session emission (e.g. from a background refreshSession
      // call) from resetting the picker back to the original value.
      if (session?.distributor && !this.distributorControl.value) {
        this.distributorControl.patchValue(session.distributor);
      }
    })
  );

  async complete() {
    const picked = this.distributorControl.value;
    // Need the latest session value here to compare the picked distributor
    // against the one the session was created with. session$ is a
    // BehaviorSubject, so firstValueFrom resolves on the same tick.
    const session = await firstValueFrom(this.sessionSvc.getCurrentSession());

    // If the user picked a different distributor than the session was created
    // with, downstream selections (policy filtering, plan pricing, asset access)
    // were all scoped to the old one — start fresh. The backend session row is
    // orphaned to TTL cleanup; customer info is moved to data$ first so the
    // customer step can pre-fill the form without requiring re-entry.
    if (session?.distributorId && session.distributorId !== picked?.id) {
      this.sessionSvc.clearSession();
      this.toastSvc.show({
        type: 'info',
        title: 'Switched distributor',
        text: `Starting a fresh provision for ${picked?.name ?? 'this distributor'}.`,
      });
    }

    this.sessionSvc.distributor$.next(picked);
    this.navigateTo('customer');
  }

  async skip() {
    const session = await firstValueFrom(this.sessionSvc.getCurrentSession());

    // Clear the session so the stale distributorId is not picked up by the
    // customer step's updateSession path, which would silently re-attach the
    // old distributor.
    if (session) {
      this.sessionSvc.clearSession();
    }

    this.distributorControl.reset(null, { emitEvent: false });
    this.sessionSvc.distributor$.next(null);
    this.sessionSvc.data$.next({
      ...this.sessionSvc.data$.value,
      distributor: undefined,
      distributorId: undefined,
    });
    this.distributor.emit(null);
    this.navigateTo('customer');
  }

  private navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }
}
